
import React, { useMemo, useState } from "react";

/*
  CourseDetailPage-PremiumVariant.js
  Premium University Course Detail Page — Alternative Design
  React + Tailwind CSS only

  Visual direction:
  - Editorial university prospectus
  - Light-first palette with selective deep navy sections
  - Asymmetric layouts
  - Programme dossier / academic journal styling
  - Burgundy #8B1E3F only
*/

const cx = (...classes) => classes.filter(Boolean).join(" ");

const ArrowUpRight = ({ className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Check = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Download = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Calendar = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <rect x="4" y="6" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 3v5M16 3v5M4 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

const Book = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H11v17H7.5A3.5 3.5 0 0 0 4 22V5.5ZM20 5.5A3.5 3.5 0 0 0 16.5 2H13v17h3.5A3.5 3.5 0 0 1 20 22V5.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
);

const Briefcase = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="M8 7V5.8A1.8 1.8 0 0 1 9.8 4h4.4A1.8 1.8 0 0 1 16 5.8V7M4 8h16v11H4V8Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M4 12h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

const Globe = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M4 12h16M12 4a13 13 0 0 1 0 16M12 4a13 13 0 0 0 0 16" stroke="currentColor" strokeWidth="1.7" />
    </svg>
);

const Users = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="17" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M3.5 19c.4-3.2 2.5-5 5.5-5s5.1 1.8 5.5 5M14 15.2c2.8-.5 5 .7 5.8 3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
);

const Chevron = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
        <path d="m8 10 4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Eyebrow = ({ children, dark = false }) => (
    <div className={cx(
        "flex items-center gap-3 text-sm font-bold uppercase tracking-[0.16em]",
        dark ? "text-[#D5AD57]" : "text-[#8B1E3F]"
    )}>
        <span className="h-px w-10 bg-current" />
        <span>{children}</span>
    </div>
);

const sampleCourse = {
    school: "School of Information & Technology",
    title: "Bachelor of Computer Applications (BCA)",
    shortTitle: "BCA",
    level: "Undergraduate",
    duration: "3 Years",
    mode: "Full Time",
    location: "ISBM University",
    eligibility: "10+2 or equivalent from a recognised board, subject to University norms.",
    intake: "As per University norms",
    heroImage: "",
    intro:
        "A future-focused undergraduate programme designed to develop computing knowledge, software capabilities, analytical thinking and practical technology skills for the digital economy.",
    overview:
        "The Bachelor of Computer Applications programme combines academic foundations with applied computing practice. Students develop an understanding of programming, databases, web technologies, software engineering, networks and contemporary digital systems through classroom learning, laboratory work, guided projects and career-oriented development.",
    advantages: [
        "Strong computing foundations",
        "Applied programming practice",
        "Project and laboratory learning",
        "Emerging technology exposure",
        "Communication and professional skills",
        "Higher-study and career pathways",
    ],
    curriculum: [
        {
            year: "Year 01",
            title: "Foundations",
            description: "Build core knowledge in computing, programming, mathematics and communication.",
            subjects: ["Computer Fundamentals", "Programming Fundamentals", "Mathematics", "Communication Skills", "Digital Systems"],
        },
        {
            year: "Year 02",
            title: "Application",
            description: "Develop stronger technical capabilities through databases, networks, web and software engineering.",
            subjects: ["Data Structures", "Database Management Systems", "Computer Networks", "Web Technologies", "Software Engineering"],
        },
        {
            year: "Year 03",
            title: "Integration",
            description: "Apply knowledge through advanced subjects, emerging technologies and project work.",
            subjects: ["Advanced Programming", "Cloud Fundamentals", "Cybersecurity Basics", "Elective", "Major Project"],
        },
    ],
    learningModel: [
        ["01", "Learn", "Structured teaching builds the conceptual foundation required for progressive technical learning."],
        ["02", "Practice", "Laboratory sessions and guided exercises strengthen practical understanding."],
        ["03", "Create", "Projects encourage students to design, build and present functional solutions."],
        ["04", "Reflect", "Reviews, presentations and feedback help improve technical and professional confidence."],
        ["05", "Connect", "Industry-oriented activities introduce students to contemporary professional expectations."],
    ],
    outcomes: [
        "Apply computing concepts in practical contexts.",
        "Develop structured software solutions.",
        "Work with databases and digital systems.",
        "Understand networks and modern IT environments.",
        "Communicate technical ideas effectively.",
        "Prepare for technology careers and higher studies.",
    ],
    careers: [
        {
            title: "Software & Application Development",
            roles: ["Junior Developer", "Application Support", "QA Associate"],
        },
        {
            title: "Web & Digital Technology",
            roles: ["Web Developer", "Front-End Associate", "Digital Solutions Executive"],
        },
        {
            title: "Data & Systems Support",
            roles: ["Database Support", "Junior Data Analyst", "IT Support"],
        },
        {
            title: "Higher Studies",
            roles: ["MCA", "M.Sc. IT", "Specialised Technology Programmes"],
        },
    ],
    admissions: [
        ["01", "Review Eligibility", "Check programme-specific eligibility and current admission guidelines."],
        ["02", "Apply Online", "Complete the online application with accurate personal and academic details."],
        ["03", "Submit Documents", "Provide the required academic and identity documents for verification."],
        ["04", "Counselling", "Receive programme and admission guidance from the University team."],
        ["05", "Confirm Admission", "Complete applicable admission formalities and fee payment."],
    ],
    faq: [
        {
            question: "Who can apply for this programme?",
            answer: "Applicants should have completed 10+2 or an equivalent examination from a recognised board, subject to current University eligibility requirements.",
        },
        {
            question: "Does the programme include practical learning?",
            answer: "Yes. The programme may include laboratory sessions, assignments, mini projects, presentations and major project work as prescribed in the academic structure.",
        },
        {
            question: "What career opportunities can I explore after graduation?",
            answer: "Graduates may explore entry-level opportunities in software, web technologies, application support, data support and IT services, or continue into relevant postgraduate programmes.",
        },
        {
            question: "Can I receive admission guidance before applying?",
            answer: "Yes. Prospective students may contact the admissions team for programme, eligibility and application guidance.",
        },
    ],
    relatedCourses: [
        {
            title: "B.Sc. (Hons) in Computer Science",
            level: "Undergraduate",
            duration: "3 Years",
            href: "/course/bsc-in-computer-science",
        },
        {
            title: "B.Sc. in Data Science",
            level: "Undergraduate",
            duration: "3 Years",
            href: "/course/bsc-in-data-science",
        },
        {
            title: "Master of Computer Applications",
            level: "Postgraduate",
            duration: "2 Years",
            href: "/course/master-of-computer-applications",
        },
    ],
};

export default function CourseDetails2({
    course = sampleCourse,
    applyHref = "/apply-online",
    brochureHref = "/brochure.pdf",
}) {
    const [activeYear, setActiveYear] = useState(0);
    const [openFaq, setOpenFaq] = useState(0);

    const selectedYear = useMemo(
        () => course.curriculum?.[activeYear] || course.curriculum?.[0],
        [course.curriculum, activeYear]
    );

    return (
        <main className="bg-white text-[#132337]">
            {/* HERO — EDITORIAL PROSPECTUS */}
            <section className="relative overflow-hidden bg-[#F7F2E8]">
                <div className="absolute inset-y-0 right-0 hidden w-[42%] bg-[#071A2F] lg:block" />
                <div className="absolute left-0 top-0 h-full w-px bg-[#071A2F]/10" />

                <div className="mx-auto w-[min(1380px,calc(100%-48px))] py-8 max-sm:w-[calc(100%-28px)]">
                    <nav className="flex flex-wrap items-center gap-2 text-sm text-[#071A2F]/50" aria-label="Breadcrumb">
                        <a href="/" className="hover:text-[#8B1E3F]">Home</a>
                        <span>/</span>
                        <a href="/programmes" className="hover:text-[#8B1E3F]">Programmes</a>
                        <span>/</span>
                        <span className="font-semibold text-[#071A2F]">{course.shortTitle}</span>
                    </nav>
                </div>

                <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)] lg:grid-cols-[1.08fr_.92fr]">
                    <div className="py-14 pr-0 sm:py-20 lg:pr-16 lg:py-24">
                        <Eyebrow>{course.school}</Eyebrow>

                        <h1 className="mt-7 max-w-[900px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(46px,6.2vw,88px)] font-normal leading-[.98] tracking-[-0.05em] text-[#071A2F]">
                            {course.title}
                        </h1>

                        <p className="mt-7 max-w-[760px] text-base leading-8 text-[#132337]/65 sm:text-lg">
                            {course.intro}
                        </p>

                        <div className="mt-9 flex flex-wrap gap-3">
                            <a
                                href={applyHref}
                                className="inline-flex min-h-[54px] items-center gap-3 rounded-full bg-[#8B1E3F] px-7 text-sm font-bold text-white transition hover:-translate-y-1 hover:bg-[#071A2F]"
                            >
                                Apply Now <ArrowUpRight />
                            </a>

                            <a
                                href={brochureHref}
                                className="inline-flex min-h-[54px] items-center gap-3 rounded-full border border-[#071A2F]/20 bg-white px-7 text-sm font-bold text-[#071A2F] transition hover:border-[#D5AD57]"
                            >
                                <Download /> Download Brochure
                            </a>
                        </div>

                        <div className="mt-12 grid gap-5 border-t border-[#071A2F]/15 pt-7 sm:grid-cols-3">
                            {[
                                ["Duration", course.duration],
                                ["Study Mode", course.mode],
                                ["Level", course.level],
                            ].map(([label, value]) => (
                                <div key={label}>
                                    <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">{label}</span>
                                    <strong className="mt-2 block text-lg text-[#071A2F]">{value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative min-h-[520px] overflow-hidden bg-[#071A2F] lg:min-h-[720px]">
                        {course.heroImage ? (
                            <img src={course.heroImage} alt="" className="absolute inset-0 h-full w-full object-cover opacity-75" />
                        ) : (
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(213,173,87,.25),transparent_28%),linear-gradient(145deg,#0C2744,#071A2F)]" />
                        )}

                        <div className="absolute inset-0 bg-gradient-to-t from-[#071A2F] via-[#071A2F]/25 to-transparent" />

                        <div className="absolute left-7 top-7 border border-white/15 bg-white/[0.06] px-5 py-4 backdrop-blur">
                            <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">Programme Code</span>
                            <strong className="mt-2 block font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-white">{course.shortTitle}</strong>
                        </div>

                        <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
                            <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">Programme Eligibility</span>
                            <p className="mt-3 max-w-xl text-base leading-8 text-white/70">{course.eligibility}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PROGRAMME DOSSIER */}
            <section className="py-20 sm:py-24 lg:py-28">
                <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[320px_1fr]">
                    <aside className="lg:sticky lg:top-24 lg:self-start">
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">Programme Dossier</span>
                        <h2 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.1] text-[#071A2F]">
                            The essential academic picture.
                        </h2>

                        <div className="mt-8 border-t-2 border-[#071A2F]">
                            {[
                                ["School", course.school],
                                ["Level", course.level],
                                ["Duration", course.duration],
                                ["Mode", course.mode],
                                ["Campus", course.location],
                                ["Intake", course.intake],
                            ].map(([label, value]) => (
                                <div key={label} className="border-b border-[#071A2F]/15 py-4">
                                    <span className="text-sm font-bold uppercase tracking-[0.1em] text-[#65717D]">{label}</span>
                                    <p className="mt-2 text-sm font-semibold leading-6 text-[#071A2F]">{value}</p>
                                </div>
                            ))}
                        </div>
                    </aside>

                    <div>
                        <Eyebrow>Programme Overview</Eyebrow>
                        <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,66px)] font-normal leading-[1.05] text-[#071A2F]">
                            Designed for strong foundations,
                            <em className="text-[#8B1E3F]"> practical confidence and progression.</em>
                        </h2>

                        <p className="mt-7 max-w-4xl text-base leading-8 text-[#65717D]">
                            {course.overview}
                        </p>

                        <div className="mt-10 grid border-l border-t border-[#071A2F]/15 sm:grid-cols-2 lg:grid-cols-3">
                            {course.advantages?.map((item, index) => (
                                <article key={item} className="min-h-[170px] border-b border-r border-[#071A2F]/15 p-6">
                                    <span className="text-sm font-bold text-[#A77B2D]">0{index + 1}</span>
                                    <h3 className="mt-6 text-lg font-bold leading-7 text-[#071A2F]">{item}</h3>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CURRICULUM — YEARBOOK STYLE */}
            <section className="bg-[#EEE5D3] py-20 sm:py-24 lg:py-28">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <header className="grid gap-8 border-b-2 border-[#071A2F] pb-10 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
                        <div>
                            <Eyebrow>Curriculum Journey</Eyebrow>
                            <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,66px)] font-normal leading-[1.05] text-[#071A2F]">
                                A progressive academic
                                <em className="text-[#8B1E3F]"> journey across three years.</em>
                            </h2>
                        </div>

                        <p className="text-base leading-8 text-[#65717D]">
                            Replace the sample subjects below with the approved curriculum for each programme.
                        </p>
                    </header>

                    <div className="mt-10 grid gap-7 lg:grid-cols-[280px_1fr]">
                        <div className="space-y-3">
                            {course.curriculum?.map((item, index) => {
                                const active = activeYear === index;
                                return (
                                    <button
                                        key={item.year}
                                        type="button"
                                        onClick={() => setActiveYear(index)}
                                        className={cx(
                                            "w-full border px-5 py-5 text-left transition",
                                            active
                                                ? "border-[#071A2F] bg-[#071A2F] text-white"
                                                : "border-[#071A2F]/15 bg-white text-[#071A2F] hover:border-[#8B1E3F]"
                                        )}
                                    >
                                        <span className={cx("text-sm font-bold uppercase tracking-[0.12em]", active ? "text-[#D5AD57]" : "text-[#8B1E3F]")}>
                                            {item.year}
                                        </span>
                                        <strong className="mt-2 block font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal">{item.title}</strong>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="border border-[#071A2F]/15 bg-white">
                            <div className="border-b border-[#071A2F]/15 p-7 sm:p-9">
                                <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">{selectedYear?.year}</span>
                                <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal text-[#071A2F]">{selectedYear?.title}</h3>
                                <p className="mt-4 max-w-3xl text-base leading-8 text-[#65717D]">{selectedYear?.description}</p>
                            </div>

                            <div className="grid sm:grid-cols-2">
                                {selectedYear?.subjects?.map((subject, index) => (
                                    <div key={subject} className="flex items-center gap-4 border-b border-r border-[#071A2F]/10 p-5">
                                        <span className="text-sm font-bold text-[#A77B2D]">0{index + 1}</span>
                                        <strong className="text-sm font-semibold text-[#071A2F]">{subject}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* LEARNING MODEL — HORIZONTAL JOURNEY */}
            <section className="py-20 sm:py-24 lg:py-28">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <header className="mx-auto max-w-4xl text-center">
                        <Eyebrow>Learning Model</Eyebrow>
                        <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.7vw,64px)] font-normal leading-[1.05] text-[#071A2F]">
                            From understanding to
                            <em className="text-[#8B1E3F]"> confident application.</em>
                        </h2>
                    </header>

                    <div className="mt-12 grid gap-4 md:grid-cols-5">
                        {course.learningModel?.map(([n, title, text], index) => (
                            <article key={n} className={cx(
                                "relative min-h-[260px] border border-[#071A2F]/10 p-6",
                                index % 2 ? "bg-[#F7F2E8]" : "bg-white"
                            )}>
                                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-5xl font-normal text-[#8B1E3F]">{n}</span>
                                <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">{title}</h3>
                                <p className="mt-4 text-sm leading-7 text-[#65717D]">{text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUTCOMES — DARK ACADEMIC PANEL */}
            <section className="bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <div className="grid gap-12 lg:grid-cols-[.75fr_1.25fr]">
                        <div>
                            <Eyebrow dark>Graduate Outcomes</Eyebrow>
                            <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,64px)] font-normal leading-[1.05]">
                                What you can
                                <em className="text-[#D5AD57]"> develop through the programme.</em>
                            </h2>
                            <p className="mt-6 text-base leading-8 text-white/60">
                                Programme outcomes should always be aligned with the approved academic curriculum.
                            </p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            {course.outcomes?.map((item, index) => (
                                <article key={item} className="border border-white/15 bg-white/[0.04] p-6">
                                    <div className="flex items-start gap-4">
                                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#D5AD57] text-sm font-bold text-[#071A2F]">0{index + 1}</span>
                                        <p className="text-base leading-8 text-white/75">{item}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CAREER LANDSCAPE */}
            <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-28">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <header className="grid gap-8 lg:grid-cols-[1fr_.55fr] lg:items-end">
                        <div>
                            <Eyebrow>Career Landscape</Eyebrow>
                            <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,66px)] font-normal leading-[1.05] text-[#071A2F]">
                                Explore professional
                                <em className="text-[#8B1E3F]"> pathways after graduation.</em>
                            </h2>
                        </div>
                        <p className="text-base leading-8 text-[#65717D]">
                            Career opportunities depend on skills, further learning, experience and employer requirements.
                        </p>
                    </header>

                    <div className="mt-10 grid gap-5 md:grid-cols-2">
                        {course.careers?.map((career, index) => (
                            <article key={career.title} className="group border border-[#071A2F]/10 bg-white p-7 transition hover:-translate-y-1 hover:shadow-[0_22px_60px_rgba(7,26,47,.09)]">
                                <div className="flex items-center justify-between">
                                    <Briefcase className="h-7 w-7 text-[#8B1E3F]" />
                                    <span className="text-sm font-bold text-[#A77B2D]">0{index + 1}</span>
                                </div>
                                <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-[#071A2F]">{career.title}</h3>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {career.roles.map((role) => (
                                        <span key={role} className="rounded-full border border-[#071A2F]/10 bg-[#F7F2E8] px-4 py-2 text-sm font-semibold text-[#071A2F]">
                                            {role}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ACADEMIC EXPERIENCE */}
            <section className="py-20 sm:py-24 lg:py-28">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <div className="grid gap-10 lg:grid-cols-[1.15fr_.85fr]">
                        <div className="border border-[#071A2F]/10 bg-[#F7F2E8] p-7 sm:p-10">
                            <Eyebrow>Academic Experience</Eyebrow>
                            <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(38px,4.5vw,60px)] font-normal leading-[1.06] text-[#071A2F]">
                                More than a classroom
                                <em className="text-[#8B1E3F]"> learning experience.</em>
                            </h2>

                            <div className="mt-9 grid gap-5 sm:grid-cols-2">
                                {[
                                    [Book, "Academic Resources", "Access structured learning resources and guided academic support."],
                                    [Users, "Mentoring", "Faculty interaction supports academic understanding and progression."],
                                    [Globe, "Contemporary Exposure", "Explore current technology ideas and evolving professional practices."],
                                    [Briefcase, "Career Readiness", "Develop communication, project and professional preparation skills."],
                                ].map(([Icon, title, text]) => (
                                    <article key={title} className="bg-white p-6">
                                        <Icon className="h-7 w-7 text-[#8B1E3F]" />
                                        <h3 className="mt-5 text-lg font-bold text-[#071A2F]">{title}</h3>
                                        <p className="mt-3 text-sm leading-7 text-[#65717D]">{text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>

                        <aside className="bg-[#8B1E3F] p-7 text-white sm:p-10">
                            <span className="text-sm font-bold uppercase tracking-[0.12em] text-white/65">Who should choose this programme?</span>
                            <h3 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal leading-[1.08]">
                                A strong fit for curious, analytical and digitally minded learners.
                            </h3>

                            <div className="mt-8 space-y-4">
                                {[
                                    "Interested in computers and digital technology",
                                    "Enjoys logical thinking and problem solving",
                                    "Wants practical exposure to software and web technologies",
                                    "Plans to pursue technology careers or higher studies",
                                ].map((item) => (
                                    <div key={item} className="flex gap-3 border-b border-white/20 pb-4">
                                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#D5AD57]" />
                                        <span className="text-sm leading-7 text-white/75">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* ADMISSION JOURNEY */}
            <section className="bg-[#EEE5D3] py-20 sm:py-24 lg:py-28">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <header className="text-center">
                        <Eyebrow>Admission Journey</Eyebrow>
                        <h2 className="mx-auto mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,64px)] font-normal leading-[1.05] text-[#071A2F]">
                            A clear path from
                            <em className="text-[#8B1E3F]"> interest to enrolment.</em>
                        </h2>
                    </header>

                    <div className="mt-12 grid gap-4 md:grid-cols-5">
                        {course.admissions?.map(([n, title, text]) => (
                            <article key={n} className="bg-white p-6">
                                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal text-[#D5AD57]">{n}</span>
                                <h3 className="mt-5 text-lg font-bold text-[#071A2F]">{title}</h3>
                                <p className="mt-3 text-sm leading-7 text-[#65717D]">{text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 sm:py-24 lg:py-28">
                <div className="mx-auto grid w-[min(1280px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.7fr_1.3fr]">
                    <div>
                        <Eyebrow>Frequently Asked Questions</Eyebrow>
                        <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(38px,4.5vw,60px)] font-normal leading-[1.05] text-[#071A2F]">
                            Important information before
                            <em className="text-[#8B1E3F]"> you apply.</em>
                        </h2>
                    </div>

                    <div className="border-t-2 border-[#071A2F]">
                        {course.faq?.map((item, index) => {
                            const open = openFaq === index;

                            return (
                                <article key={item.question} className="border-b border-[#071A2F]/15">
                                    <button
                                        type="button"
                                        onClick={() => setOpenFaq(open ? -1 : index)}
                                        className="grid min-h-[92px] w-full grid-cols-[46px_1fr_42px] items-center gap-4 py-5 text-left"
                                    >
                                        <span className="text-sm font-bold text-[#A77B2D]">0{index + 1}</span>
                                        <span className="font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal leading-7 text-[#071A2F] sm:text-2xl">{item.question}</span>
                                        <span className={cx(
                                            "grid h-10 w-10 place-items-center border border-[#071A2F]/15 transition",
                                            open ? "bg-[#8B1E3F] text-white" : "text-[#071A2F]"
                                        )}>
                                            <Chevron className={cx("transition", open ? "rotate-180" : "")} />
                                        </span>
                                    </button>

                                    <div className={cx("grid transition-all duration-300", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
                                        <div className="overflow-hidden">
                                            <p className="mb-6 ml-[62px] max-w-3xl text-sm leading-8 text-[#65717D]">{item.answer}</p>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* RELATED PROGRAMMES */}
            <section className="bg-[#F7F2E8] py-20 sm:py-24">
                <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                    <div className="flex flex-col justify-between gap-6 border-b border-[#071A2F]/15 pb-8 sm:flex-row sm:items-end">
                        <div>
                            <Eyebrow>Related Programmes</Eyebrow>
                            <h2 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4.2vw,56px)] font-normal text-[#071A2F]">
                                Continue exploring your academic options.
                            </h2>
                        </div>
                        <a href="/programmes" className="inline-flex items-center gap-3 text-sm font-bold text-[#8B1E3F]">
                            View all programmes <ArrowUpRight />
                        </a>
                    </div>

                    <div className="mt-8 grid gap-5 md:grid-cols-3">
                        {course.relatedCourses?.map((item) => (
                            <a key={item.title} href={item.href} className="group border border-[#071A2F]/10 bg-white p-6 transition hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(7,26,47,.08)]">
                                <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">{item.level}</span>
                                <h3 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-8 text-[#071A2F]">{item.title}</h3>
                                <div className="mt-8 flex items-center justify-between border-t border-[#071A2F]/10 pt-5">
                                    <span className="text-sm text-[#65717D]">{item.duration}</span>
                                    <span className="grid h-10 w-10 place-items-center rounded-full bg-[#071A2F] text-[#D5AD57] transition group-hover:bg-[#8B1E3F] group-hover:text-white">
                                        <ArrowUpRight />
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-[#071A2F] py-14 text-white">
                <div className="mx-auto flex w-[min(1380px,calc(100%-48px))] flex-col justify-between gap-7 max-sm:w-[calc(100%-28px)] sm:flex-row sm:items-center">
                    <div>
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#D5AD57]">Admissions Open</span>
                        <h2 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal sm:text-4xl">
                            Ready to begin your university journey?
                        </h2>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <a href={applyHref} className="inline-flex min-h-[52px] items-center gap-3 rounded-full bg-[#8B1E3F] px-6 text-sm font-bold text-white transition hover:bg-[#D5AD57] hover:text-[#071A2F]">
                            Apply Now <ArrowUpRight />
                        </a>
                        <a href={brochureHref} className="inline-flex min-h-[52px] items-center gap-3 rounded-full border border-white/25 px-6 text-sm font-bold text-white transition hover:border-[#D5AD57] hover:text-[#D5AD57]">
                            <Download /> Download Brochure
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}
